import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRooms } from 'services/api';
import { scheduleActions } from 'features/schedule/redux';
import { useLazyGetTeacherWorkloadsQuery } from 'shared/api';
import { useAppForm } from 'shared/UI/UIKit/Form';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import type { ScheduleLesson, Teacher, ScheduleRoom } from 'services/api';
import type { Room } from 'services/api/rooms/types';
import type { TeacherWorkload } from 'shared/api';

export type LessonFormValues = {
  teacherId: number;
  roomId: number;
};

function mapRoomToScheduleRoom(r: Room): ScheduleRoom {
  return {
    id: r.id,
    name: r.name,
    number: r?.number ?? 0,
    capacity: 0,
    building: {
      id: r.buildingId,
      name: '',
      address: '',
    },
  };
}

function mapTeacherWorkloadToTeacher(tw: TeacherWorkload): Teacher {
  return { id: tw.id, code: tw.code };
}

export const useLessonModal = (lesson: ScheduleLesson, open: boolean, onClose: () => void) => {
  const dispatch = useAppDispatch();
  const [form] = useAppForm<LessonFormValues>();
  const [fetchTeacherWorkloads] = useLazyGetTeacherWorkloadsQuery();

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [rooms, setRooms] = useState<ScheduleRoom[]>([]);
  const [loading, setLoading] = useState(false);

  const [addTeacherWorkloadLessonModalVisible, setAddTeacherWorkloadLessonModalVisible] =
    useState(false);
  const [addRoomLessonModalVisible, setAddRoomLessonModalVisible] = useState(false);

  const loadTeachers = useCallback(
    () =>
      fetchTeacherWorkloads({ size: 1000 })
        .unwrap()
        .then((data) => {
          setTeachers(data.content.map(mapTeacherWorkloadToTeacher));
        }),
    [fetchTeacherWorkloads],
  );

  const loadRooms = useCallback(
    () => getRooms().then((res) => setRooms(res.content.map(mapRoomToScheduleRoom))),
    [],
  );

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    Promise.all([loadTeachers(), loadRooms()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [loadRooms, loadTeachers, open]);

  const teacherOptions = useMemo(
    () => teachers.map((t) => ({ value: t.id, label: t.code })),
    [teachers],
  );
  const roomOptions = useMemo(
    () => rooms.map((r) => ({ value: r.id, label: `${r.number} - ${r.name}` })),
    [rooms],
  );

  useEffect(() => {
    if (lesson) {
      form.setFieldsValue({
        teacherId: lesson.teacher.id,
        roomId: lesson.room.id,
      });
    }
  }, [lesson, form]);

  const handleSave = useCallback(() => {
    form.validateFields().then((values: LessonFormValues) => {
      const teacher = teachers.find((t) => t.id === values.teacherId);
      const room = rooms.find((r) => r.id === values.roomId);

      if (!teacher || !room) return;

      dispatch(
        scheduleActions.applyDraftLessonPatch({
          lessonId: lesson.id,
          patch: {
            teacher,
            room,
          },
        }),
      );

      onClose();
    });
  }, [dispatch, form, lesson, onClose, rooms, teachers]);

  return {
    state: {
      form,
      teachers,
      rooms,
      teacherOptions,
      roomOptions,
      loading,
      addTeacherWorkloadLessonModalVisible,
      addRoomLessonModalVisible,
      isEditScheduleLoading: false,
    },
    methods: {
      handleSave,
      refreshTeachers: loadTeachers,
      refreshRooms: loadRooms,
      setAddTeacherWorkloadLessonModalVisible,
      setAddRoomLessonModalVisible,
    },
  };
};
